import { type FC } from "react";
import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { DialogHeader } from "@/shared/components";
import {
  useDeleteStreamEntrypointsMutation,
  useUpdateStreamEntrypointMutation,
  type StreamEntrypoint,
} from "@/partner/entities/streamEntrypoints";
import type { StreamEntrypointFormValues, StreamEntrypointTab } from "../model";
import { StreamEntrypointTabsMenu } from "./StreamEntrypointTabsMenu";
import { StreamEntrypointEditTab, FORM_ID } from "./StreamEntrypointEditTab";
import { StreamEntrypointBroadcastTab } from "./StreamEntrypointBroadcastTab";
import { StreamEntrypointMonitoringTab } from "./StreamEntrypointMonitoringTab";

interface StreamEntrypointFormProps {
  entrypoint: StreamEntrypoint;
  tab: StreamEntrypointTab;
  onClose: () => void;
}

// Шелл по образцу EditStreamDialog.tsx + StreamDialogHeader (streams-фича):
// один DialogHeader сверху (крестик, заголовок, Save только на вкладке
// edit), табы под ним, свитч вкладок ниже. Мутации редактирования подняты
// сюда из StreamEntrypointEditTab, чтобы Save-кнопка хедера видела их
// loading/disabled независимо от того, какая вкладка сейчас открыта.
export const StreamEntrypointForm: FC<StreamEntrypointFormProps> = ({
  entrypoint,
  tab,
  onClose,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [updateStreamEntrypoint, { loading: updating }] =
    useUpdateStreamEntrypointMutation({
      refetchQueries: ["StreamEntrypointsList"],
      onCompleted: () => {
        enqueueSnackbar(t("streamEntrypoints.updated"), {
          variant: "success",
        });
      },
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    });

  const [deleteStreamEntrypoints, { loading: deleting }] =
    useDeleteStreamEntrypointsMutation({
      refetchQueries: ["StreamEntrypointsList"],
      onCompleted: () => {
        enqueueSnackbar(t("streamEntrypoints.deleted"), {
          variant: "success",
        });
        onClose();
      },
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    });

  const handleDelete = () => {
    if (!window.confirm(t("streamEntrypoints.deleteConfirm"))) return;
    void deleteStreamEntrypoints({ variables: { ids: [entrypoint.id] } });
  };

  const handleFormSubmit = async (values: StreamEntrypointFormValues) => {
    await updateStreamEntrypoint({
      variables: { id: entrypoint.id, input: { title: values.title } },
    });
  };

  return (
    <>
      <DialogHeader
        title={entrypoint.title}
        onClose={onClose}
        loading={updating || deleting}
        actions={
          tab === "edit" ? (
            <Button
              variant="contained"
              type="submit"
              form={FORM_ID}
              disabled={updating}
            >
              {t("common.actions.save")}
            </Button>
          ) : undefined
        }
      />

      <Box>
        <StreamEntrypointTabsMenu tab={tab} />
      </Box>
      <Box
        sx={{
          p: 1,
          overflow: "auto",
          minHeight: 600,
          maxHeight: "calc(100vh - 64px)",
        }}
      >
        {tab === "edit" && (
          <StreamEntrypointEditTab
            entrypoint={entrypoint}
            onSubmit={handleFormSubmit}
            onDelete={handleDelete}
            updating={updating}
            deleting={deleting}
          />
        )}
        {tab === "broadcast" && (
          <StreamEntrypointBroadcastTab entrypoint={entrypoint} />
        )}
        {tab === "monitoring" && (
          <StreamEntrypointMonitoringTab entrypoint={entrypoint} />
        )}
      </Box>
    </>
  );
};
